import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Loader2, AlertCircle, PartyPopper } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { formatHolidayDate } from '../utils/laborCalendarUtils';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';

/**
 * Modal al hacer clic en un festivo del calendario (vista Fábrica).
 */
export function LaborHolidayClickModal({ holiday, calendarId, onClose, onDeleted }) {
  const queryClient = useQueryClient();
  const [error, setError] = React.useState(null);

  const mutation = useMutation({
    mutationFn: () => laborScheduleService.deleteHoliday(holiday.id),
    onSuccess: async () => {
      if (calendarId) {
        await queryClient.invalidateQueries({ queryKey: ['labor-holidays', calendarId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-pattern-list'] });
      onDeleted?.();
      onClose();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudo eliminar el festivo');
    },
  });

  if (!holiday) return null;

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={mutation.isPending}
        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
      >
        Cerrar
      </button>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mutation.mutate();
        }}
        disabled={mutation.isPending || !holiday.id}
        title={!holiday.id ? 'Recarga la página o vuelve a abrir el calendario para obtener el ID' : undefined}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Eliminar festivo
      </button>
    </>
  );

  return (
    <LaborCalendarModalShell
      isOpen={Boolean(holiday)}
      onClose={onClose}
      title="Festivo"
      icon={PartyPopper}
      footer={footer}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-gray-500 text-xs mb-1">Nombre</dt>
          <dd className="text-white font-medium">{holiday.name}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs mb-1">Fecha</dt>
          <dd className="text-white">{formatHolidayDate(holiday.date)}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs mb-1">ID</dt>
          <dd className="text-gray-300">{holiday.id ?? '— (lista de festivos no cargada)'}</dd>
        </div>
      </dl>

      {!holiday.id && (
        <p className="mb-2 text-xs text-amber-400/90">
          No se pudo resolver el ID del festivo. Comprueba que el calendario activo coincide con el
          usado al crear el festivo.
        </p>
      )}

      <p className="mt-4 text-xs text-gray-500 flex items-start gap-2">
        <Trash2 className="w-4 h-4 shrink-0 text-gray-400" />
        Al eliminar, el día volverá a ser laborable en el calendario de planta.
      </p>
    </LaborCalendarModalShell>
  );
}
